import { Fragment, useEffect, useState, useMemo } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useHttpClient } from '../../shared/http-client';
import { classNames, noop } from '../../utils';
import { FieldError } from '../../elements/field-error/field-error';
import { Button } from '../../elements/button';
import { SelectFullname } from '../../elements/select-fullname';
import { DatePicker } from '../../elements/date-picker';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import { obtenerHoraDesdeFecha } from '../../utils';

import 'leaflet/dist/leaflet.css';
import { divIcon, Point } from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import { EyeIcon } from '@heroicons/react/outline';

type Punto = { lat: number; lng: number; hora: string };
type Pintado = { weight: number; opacity: number; ptos: Punto[] };
type RutaData = {
    tipo: string;
    color: string;
    latCenter: string;
    lonCenter: string;
    zoom: string;
    lat1: string;
    lon1: string;
    lat2: string;
    lon2: string;
    pintado: Pintado[];
};




const createNumberedIcon = (number: number, color: string) => {
    const markerHtml = ReactDOMServer.renderToStaticMarkup(
        <div style={{ position: 'relative', width: '32px', height: '32px' }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
                <path fill={color} d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </svg>
        </div>
    );

    return divIcon({
        html: markerHtml,
        className: 'custom-div-icon',
        iconSize: new Point(32, 32),
        iconAnchor: new Point(16, 32),
        popupAnchor: new Point(0, -32),
    });
};

const formatHora = (hora: string | undefined): string => {
    if (!hora || hora.length < 4) {
        return hora || '';
    }
    const hours = hora.substring(0, 2);
    const minutes = hora.substring(2, 4);
    return `${hours}:${minutes}`;
};

const validacion = Yup.object().shape({
    driver: Yup.mixed().required('obligatorio'),
    date: Yup.string().required('obligatorio'),
});

const initialValues = {
    driver: null,
    date: '',
};

export const MapaGpsDia: React.FC = () => {
    const { t } = useTranslation() as { t: (key: string) => string };
    const { api, state } = useHttpClient();
    const navigate = useNavigate();
    const [drivers, setDrivers] = useState<any[]>([]);
    const [dataRoute, setDataRoute] = useState<any | null>(null);
    const [dataStop, setDataStop] = useState<any | null>(null);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [selectedVehicleIndex, setSelectedVehicleIndex] = useState<number>(0);
    const [visibleRoutes, setVisibleRoutes] = useState<boolean[]>([]);
    const [visibleStops, setVisibleStops] = useState<boolean[]>([]);
    const [viewRoutes, setViewRoutes] = useState<boolean>(true);
    const [noDatos, setNoDatos] = useState<boolean>(false);

    useEffect(() => {
        api('/fleet/drivers/', 'GET');
    }, []);

    const onSubmit = (values: any) => {
        api(`/fleet/drivers/${values.driver}/trips/?date=${values.date}`, 'GET');
        //api(`/fleet/drivers/1/trips/?date=2025-08-02`, 'GET');
    };

    useEffect(() => {
        if (state.data && state.path.includes('drivers')) {
            if (Array.isArray(state.data.results)) {
                setDrivers(state.data.results);
            }
            setVehicles([]);
            setDataRoute(null);
            setDataStop(null);
        }
        if (state.data && state.path.includes('trips') && state.data.vehicles) {
            setVehicles(state.data.vehicles);

            if (state.data.vehicles.length > 0) {
                setSelectedVehicleIndex(0);
                const vehicle = state.data.vehicles[0];


                if (vehicle.trips.length > 0) {
                    setDataRoute(vehicle.trips);
                    setVisibleRoutes(new Array(vehicle.trips.length).fill(true));
                    setDataStop(vehicle.stops);
                    setVisibleStops(new Array(vehicle.stops.length).fill(true));
                    setNoDatos(false);
                } else {
                    setDataRoute(null);
                    setVisibleRoutes([]);
                    setDataStop(null);
                    setVisibleStops([]);
                    setNoDatos(true);
                }
            } else {
                setDataRoute(null);
                setDataStop(null);
                setNoDatos(true);
            }
        }
        if (state.error) {
            if (state.error.error?.includes('No vehicle assignments found for driver')) {
                toast.error(`${t('common.error.driver-not-asigned')}`);
            }
        }
    }, [state.data, state.error, state.path, t]);

    const handleVehicleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newIndex = parseInt(event.target.value, 10);
        setSelectedVehicleIndex(newIndex);
        const newVehicle = vehicles[newIndex];
        if (newVehicle.trips && newVehicle.trips.length > 0) {
            setDataRoute(newVehicle.trips);
            setVisibleRoutes(new Array(newVehicle.trips.length).fill(true));
            setDataStop(newVehicle.stops);
            setVisibleStops(new Array(newVehicle.stops.length).fill(true));
        } else {
            setDataRoute(null);
            setVisibleRoutes([]);
            setDataStop(null);
            setVisibleStops([]);
        }
    };

    const handleRouteVisibilityChange = (index: number) => {
        setVisibleRoutes(prev => {
            const newVisibleRoutes = [...prev];
            newVisibleRoutes[index] = !newVisibleRoutes[index];
            return newVisibleRoutes;
        });
    };

    const handleStopVisibilityChange = (index: number) => {
        setVisibleStops(prev => {
            const newVisibleStops = [...prev];
            newVisibleStops[index] = !newVisibleStops[index];
            return newVisibleStops;
        });
    };

    const bounds: [[number, number], [number, number]] = useMemo(() => {
        if (!dataRoute || dataRoute.length === 0) {
            return [[0, 0], [0, 0]];
        }

        const allVisibleCoordinates = dataRoute.reduce((acc: any[], route: any, index: number) => {
            if (visibleRoutes[index] && route.coordinates) {
                const validCoords = route.coordinates.filter((p: any) => p && typeof p.latitude === 'number' && typeof p.longitude === 'number');
                return acc.concat(validCoords);
            }
            return acc;
        }, []);

        if (allVisibleCoordinates.length === 0) {
            return [[40.416775, -3.703790], [40.416775, -3.703790]];
        }

        const latitudes = allVisibleCoordinates.map((p: any) => p.latitude);
        const longitudes = allVisibleCoordinates.map((p: any) => p.longitude);

        const minLat = Math.min(...latitudes);
        const maxLat = Math.max(...latitudes);
        const minLng = Math.min(...longitudes);
        const maxLng = Math.max(...longitudes);

        return [
            [minLat, minLng],
            [maxLat, maxLng]
        ];
    }, [dataRoute]);

    const boundsStop: [[number, number], [number, number]] = useMemo(() => {
        if (!dataStop || dataStop.length === 0) {
            return [[0, 0], [0, 0]];
        }

        const allVisibleStops = dataStop.filter((stop: any, index: number) =>
            visibleStops[index] && stop && typeof stop.lat === 'number' && typeof stop.lon === 'number'
        );

        if (allVisibleStops.length === 0) {
            return [[40.416775, -3.703790], [40.416775, -3.703790]];
        }

        const latitudes = allVisibleStops.map((p: any) => p.lat);
        const longitudes = allVisibleStops.map((p: any) => p.lon);

        const minLat = Math.min(...latitudes);
        const maxLat = Math.max(...latitudes);
        const minLng = Math.min(...longitudes);
        const maxLng = Math.max(...longitudes);

        return [
            [minLat, minLng],
            [maxLat, maxLng]
        ];
    }, [dataStop]);

    const renderForm = () => (
        <div className="relative z-10">
            <Formik
                initialValues={initialValues}
                onSubmit={onSubmit}
                validationSchema={validacion}
                enableReinitialize
            >
                {({ values, errors, touched, setFieldValue, handleSubmit }) => (
                    <form onSubmit={handleSubmit} autoComplete="off">
                        <div className="px-8 pt-4 text-[22px] text-gray-900">
                            {t('common.label.mapa-gps-dia')}
                        </div>
                        <div className="px-8 pt-4">
                            <div className="flex justify-between">
                                <div className="w-full">
                                    <SelectFullname
                                        name="driver"
                                        label={t('common.label.conductor')}
                                        placeholder="Select"
                                        items={drivers}
                                        value={values.driver}
                                        onChange={val => setFieldValue('driver', val.driver)}
                                    />
                                    <FieldError touched={touched} errors={errors} field="driver" />
                                </div>
                                <div className=" w-full pt-[3px] pl-8">
                                    <DatePicker
                                        name="date"
                                        type="date"
                                        label={t('common.labels.date')}
                                        value={values.date}
                                        onChange={(val: any) => setFieldValue('date', val.date ? dayjs(val.date).format('YYYY-MM-DD') : null)}
                                    />
                                    <FieldError touched={touched} errors={errors} field="date" />
                                </div>
                                <div className="pl-8 pt-8">
                                    <Button
                                        label={t('common.button.search')}
                                        onClick={handleSubmit}
                                        disabled={state.loading}
                                    />
                                </div>
                            </div>
                        </div>
                    </form>
                )}
            </Formik>
        </div>
    );

    const RecenterMap: React.FC<{ bounds: [[number, number], [number, number]] }> = ({ bounds }) => {
        const map = useMap();
        useEffect(() => {
            if (bounds[0][0] !== 0) {
                map.fitBounds(bounds);
            }
        }, [bounds, map]);
        return null;
    };

    const routeColors = [
        '#E6194B', '#3CB44B', '#FFE119', '#4363D8', '#F58231', '#911EB4', '#46F0F0', '#F032E6', '#228B22', '#FABEBE',
        '#008080', '#E6BEFF', '#9A6324', '#FFA500', '#800000', '#008B8B', '#808000', '#FFD8B1', '#000075', '#808080'
    ];

    return (
        <>
            {renderForm()}
            {noDatos ? <div className='flex justify-center mt-24 font-light text-[24px] text-primary'>{t('common.label.mapa-dia-no-datos')}</div> : ''}
            {dataRoute && (
                <div className='mt-4'>
                    <div className='mb-2'>
                        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
                            {vehicles.length > 0 && (
                                <div className='ml-8'>
                                    <label htmlFor='vehicle-select' className='block text-[12px] font-bold'>
                                        {t('common.label.vehiculo')}
                                    </label>
                                    <select
                                        id='vehicle-select'
                                        name='vehicle-select'
                                        className='mt-1 block w-[200px] pl-3 pr-10 py-2 text-md  border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md'
                                        value={selectedVehicleIndex}
                                        onChange={handleVehicleChange}
                                    >
                                        {vehicles.map((vehicle, index) => (
                                            <option key={index} value={index}>
                                                {vehicle.plate || `Vehículo ${index + 1}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}


                        </div>
                        <div className='ml-8 pt-4'>
                            <div className='flex flex-wrap'>
                                {vehicles[selectedVehicleIndex].distance ? <div className='font-semibold text-[12px] pr-4'>{t('common.label.distancia-recorrida')}: <span className='font-normal'>{(vehicles[selectedVehicleIndex].distance / 1000).toFixed(2)} Km</span></div> : ''}
                                {vehicles[selectedVehicleIndex].expected_distance ? <div className='font-semibold text-[12px] pr-4'>{t('common.label.distancia-recorrida-esperada')}: <span className='font-normal'>{(vehicles[selectedVehicleIndex].expected_distance / 1000).toFixed(2)} Km</span></div> : <div></div>}
                                <div className='font-semibold text-[12px] pr-4'>{t('common.label.duracion-total')}: <span className='font-normal'>{vehicles[selectedVehicleIndex].total_duration_display}</span></div>
                                <div className='font-semibold text-[12px] pr-4'>{t('common.label.duracion-viajes')}: <span className='font-normal'>{vehicles[selectedVehicleIndex].travel_duration_display}</span></div>
                                {vehicles[selectedVehicleIndex].expected_travel_duration_display ? <div className='font-semibold text-[12px] pr-4'>{t('common.label.duracion-viajes-esperada')}: <span className='font-normal'>{vehicles[selectedVehicleIndex].expected_travel_duration_display}</span></div> : ''}
                                <div className='font-semibold text-[12px] pr-4'>{t('common.label.duracion-paradas')}: <span className='font-normal'>{vehicles[selectedVehicleIndex].stop_duration_display}</span></div>
                                {vehicles[selectedVehicleIndex].total_fuel_used ? <div className='font-semibold text-[12px] pr-4'>{t('common.label.total-combustible-utilizado')}: <span className='font-normal'>{vehicles[selectedVehicleIndex].total_fuel_used} L</span></div> : ''}
                                {vehicles[selectedVehicleIndex].total_eco_score ? <div className='font-semibold text-[12px] pr-4'>{t('common.label.puntuacion_eco_total')}: <span className='font-normal'>{vehicles[selectedVehicleIndex].total_eco_score}</span></div> : ''}
                                {vehicles[selectedVehicleIndex].total_eco_score_category ? <div className='font-semibold text-[12px] pr-4'>{t('common.label.categoria_eco')}: <span className='font-normal'>{vehicles[selectedVehicleIndex].total_eco_score_category}</span></div> : ''}
                            </div>
                            <div>
                                <div className='flex pt-1'>
                                    <div className='font-semibold text-[12px] '>{t('common.form-label.hora-inicio')}: <span className='font-normal'>{obtenerHoraDesdeFecha(vehicles[selectedVehicleIndex].start_time)}</span></div>
                                    <div className='font-semibold text-[12px] pl-4'>{t('common.form-label.address-inicio')}: <span className='font-normal'>{vehicles[selectedVehicleIndex].start_address}</span></div>
                                </div>
                                <div className='flex pt-1'>
                                    <div className='font-semibold text-[12px] '>{t('common.form-label.hora-fin')}: <span className='font-normal'>{obtenerHoraDesdeFecha(vehicles[selectedVehicleIndex].end_time)}</span></div>
                                    <div className='font-semibold text-[12px] pl-4'>{t('common.form-label.address-fin')}: <span className='font-normal'>{vehicles[selectedVehicleIndex].end_address}</span></div>
                                </div>

                            </div>

                        </div>
                    </div>
                    {viewRoutes ? <>
                        <div className='pl-8 py-[10px]'>
                            {/*   <div className='text-[13px] font-bold mb-2'>{t('common.label.rutas')}</div> */}
                            <div className='flex justify-between'>
                                <div className='flex flex-row flex-wrap gap-x-4 gap-y-2'>
                                    {dataRoute?.map((_: any, index: number) => (
                                        <div key={index} className='flex items-center'>
                                            <input
                                                type='checkbox'
                                                id={`route-checkbox-${index}`}
                                                checked={visibleRoutes[index]}
                                                onChange={() => handleRouteVisibilityChange(index)}
                                                className='h-4 w-4 rounded border-gray-300 text-gray-400 focus:ring-gray-300'
                                            />
                                            <label
                                                htmlFor={`route-checkbox-${index}`}
                                                className='ml-2 block text-sm font-medium'
                                                style={{ color: routeColors[index % routeColors.length] }}
                                            >
                                                {t('common.label.ruta')} {index + 1}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <Button
                                        label={t('common.label.ver-paradas')}
                                        onClick={() => setViewRoutes(false)}
                                    />
                                </div>

                            </div>
                        </div>
                        <div style={{ height: 'calc(100vh - 400px)', width: '100%' }} className='pl-8'>
                            <MapContainer bounds={bounds} style={{ height: '100%', width: '100%' }} className="map-container">
                                <RecenterMap bounds={bounds} />
                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                {dataRoute.map((segment: any, i: number) => {
                                    console.log('segment', segment)
                                    if (!visibleRoutes[i]) {
                                        return null;
                                    }
                                    const positions = segment.coordinates
                                        .filter((p: any) => p && typeof p.latitude === 'number' && typeof p.longitude === 'number')
                                        .map((p: any) => [p.latitude, p.longitude] as [number, number]);

                                    if (positions.length === 0) {
                                        return null;
                                    }

                                    const markerPosition = positions[0];
                                    const routeColor = routeColors[i % routeColors.length];
                                    const customIcon = createNumberedIcon(i + 1, routeColor);

                                    return (
                                        <Fragment key={i}>
                                            {positions.length >= 2 && (
                                                <Polyline
                                                    positions={positions}
                                                    pathOptions={{
                                                        color: routeColor,
                                                        weight: 5,
                                                        opacity: 0.7,
                                                    }}
                                                />
                                            )}
                                            {/* @ts-ignore */}
                                            <Marker position={markerPosition} icon={customIcon}>
                                                <Popup>
                                                    <div className='flex pb-1'>
                                                        <div className='font-bold text-[14px] pt-[1px]'>{t('common.label.ruta')} {i + 1}</div>
                                                        <div className=''>
                                                            <button
                                                                className="rounded-md pl-2 "
                                                                onClick={() => navigate(`/trips/${segment.id}`)}
                                                            >
                                                                <EyeIcon
                                                                    className={classNames(
                                                                        'h-5 w-5 text-primary hover:text-gray-darke',



                                                                    )}
                                                                />
                                                            </button>
                                                        </div>
                                                    </div>


                                                    <div className='font-semibold'>{t('common.label.duracion')}: <span className='font-normal'>{segment.duration_display}</span></div>
                                                    {segment.expected_duration_display ? <div className='font-semibold'>{t('common.label.duracion-esperada')}: <span className='font-normal'>{segment.expected_duration_display}</span></div> : ''}
                                                    <div className='font-semibold'>{t('common.form-label.hora-inicio')}: <span className='font-normal'>{obtenerHoraDesdeFecha(segment.start_time)}</span></div>
                                                    <div className='font-semibold'>{t('common.form-label.hora-fin')}: <span className='font-normal'>{obtenerHoraDesdeFecha(segment.end_time)}</span></div>
                                                    {segment.avg_speed ? <div className='font-semibold'>{t('common.label.velocidad-media')}: <span className='font-normal'>{segment.avg_speed} Km/h</span></div> : <div></div>}
                                                    {segment.odometer ? <div className='font-semibold'>{t('common.label.distancia-recorrida')}: <span className='font-normal'>{(+segment.odometer / 1000).toFixed(2)} Km</span></div> : <div></div>}
                                                    {segment.expected_distance ? <div className='font-semibold'>{t('common.label.distancia-recorrida-esperada')}: <span className='font-normal'>{(+segment.expected_distance / 1000).toFixed(2)} Km</span></div> : <div></div>}
                                                    {segment.fuel_used ? <div className='font-semibold text-[12px]'>{t('common.label.total-combustible-utilizado')}: <span className='font-normal'>{segment.fuel_used} L</span></div> : ''}
                                                    {segment.eco_score ? <div className='font-semibold text-[12px]'>{t('common.label.puntuacion_eco_total')}: <span className='font-normal'>{segment.eco_score}</span></div> : ''}
                                                    {segment.eco_score_category ? <div className='font-semibold text-[12px]'>{t('common.label.categoria_eco')}: <span className='font-normal'>{segment.eco_score_category}</span></div> : ''}
                                                    <div className='font-semibold'>{t('common.form-label.address-inicio')}: <span className='font-normal'>{segment.start_address}</span></div>
                                                    <div className='font-semibold'>{t('common.form-label.address-fin')}: <span className='font-normal'>{segment.end_address}</span></div>
                                                </Popup>
                                            </Marker>
                                        </Fragment>
                                    );
                                })}
                            </MapContainer>
                        </div>
                    </> :
                        <>
                            <div className='pl-8 py-[10px]'>
                                {/*   <div className='text-[13px] font-bold mb-2'>{t('common.label.rutas')}</div> */}
                                <div className='flex justify-between'>
                                    <div className='flex flex-row flex-wrap gap-x-4 gap-y-2'>
                                        {dataStop?.map((_: any, index: number) => (
                                            <div key={index} className='flex items-center'>
                                                <input
                                                    type='checkbox'
                                                    id={`route-checkbox-${index}`}
                                                    checked={visibleStops[index]}
                                                    onChange={() => handleStopVisibilityChange(index)}
                                                    className='h-4 w-4 rounded border-gray-300 text-gray-400 focus:ring-gray-300'
                                                />
                                                <label
                                                    htmlFor={`route-checkbox-${index}`}
                                                    className='ml-2 block text-sm font-medium'
                                                    style={{ color: routeColors[index % routeColors.length] }}
                                                >
                                                    {t('common.label.parada')} {index + 1}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                    <div>
                                        <Button
                                            label={t('common.label.ver-rutas')}
                                            onClick={() => setViewRoutes(true)}
                                        />
                                    </div>

                                </div>
                            </div>
                            <div style={{ height: 'calc(100vh - 400px)', width: '100%' }} className='pl-8'>
                                <MapContainer bounds={boundsStop} style={{ height: '100%', width: '100%' }} className="map-container">
                                    <RecenterMap bounds={boundsStop} />
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                    {dataStop.map((segment: any, i: number) => {
                                        if (!visibleStops[i] || !segment || typeof segment.lat !== 'number' || typeof segment.lon !== 'number') {
                                            return null;
                                        }

                                        console.log('segment', segment)


                                        const markerPosition = [segment.lat, segment.lon] as [number, number];
                                        const routeColor = routeColors[i % routeColors.length];
                                        const customIcon = createNumberedIcon(i + 1, routeColor);

                                        return (
                                            <Fragment key={i}>

                                                {/* @ts-ignore */}
                                                <Marker position={markerPosition} icon={customIcon}>
                                                    <Popup>
                                                        <div className='font-bold text-[14px] pb-2'>{t('common.label.parada')} {i + 1}</div>
                                                        <div className='font-semibold'>{t('common.label.duracion')}: <span className='font-normal'>{segment.duration_display}</span></div>
                                                        <div className='font-semibold'>{t('common.form-label.address')}: <span className='font-normal'>{segment.address}</span></div>
                                                        <div className='font-semibold'>{t('common.label.horario-llegada')}: <span className='font-normal'>{obtenerHoraDesdeFecha(segment.arrival_time)}</span></div>
                                                        <div className='font-semibold'>{t('common.label.horario-salida')}: <span className='font-normal'>{obtenerHoraDesdeFecha(segment.departure_time)}</span></div>
                                                    </Popup>
                                                </Marker>
                                            </Fragment>
                                        );
                                    })}
                                </MapContainer>
                            </div>
                        </>
                    }
                </div>
            )}
        </>
    );
};
